/*global codePush*/
import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'
import * as React from 'react'
import ReactDOM from 'react-dom'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { MuiThemeProvider } from '@material-ui/core'
import { createBrowserHistory, createMemoryHistory } from 'history'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { isIOS, isNative } from './common/helpers/isNative'
import './i18nInit'
import { Root } from './Root/Root'
import { configureStore } from './Root/store/configureStore'
import { Theme } from './styles/material-ui-theme-v2'
import buildVersion from './version.json'
import * as serviceWorker from './serviceWorker'
import { IGlobalState } from './Root/store/appReducer'
import { dataSync, refreshActivityView } from './services/DataSync'
import { Preferences } from '@capacitor/preferences'
import { App } from '@capacitor/app'
// import { codePushCheck } from './services/CodePush'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import api from './services/api.services'
import ApiClient from './services/ApiClient'
import apiUrl from './services/apiUrl'
import { defineCustomElements } from '@ionic/pwa-elements/loader'
import { SplashScreen } from '@capacitor/splash-screen'
import { setUpPushNotifications } from './services/pushNotifications'
import { scrollToTop } from './common/helpers/scroll'
import { Device } from '@capacitor/device'
import { log } from './services/log'
import { appMetaData } from './common/auth/authSagas'

// Because hashHistory doesn't support passing state, we have to use memoryHistory for Cordova.
const history = isIOS ? createMemoryHistory() : createBrowserHistory()
//const history = isNative() ? createMemoryHistory() : createBrowserHistory()
//const history = isNative() ? createHashHistory() : createBrowserHistory()
export const { store, persistor } = configureStore({}, history)

const { version } = buildVersion || { version: '' }
Bugsnag.start({
  apiKey: '0c44d0a4cdda9cca7b400f5dc3b4504b',
  plugins: [new BugsnagPluginReact(React)],
  appVersion: version,
  onError: function (event) {
    const globalState = store.getState() as any as IGlobalState
    if (globalState && globalState.auth && globalState.auth.userDto && globalState.auth.userDto.email) {
      const userDto = globalState.auth.userDto
      event.setUser(userDto.userId)
    }
    event.addMetadata('capacitor', 'platform', Capacitor.getPlatform())
  },
  autoDetectErrors: process.env.NODE_ENV !== 'development',
  autoTrackSessions: process.env.NODE_ENV !== 'development',
})

const ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React)
const renderApp = (Component: any) => (
  // @ts-ignore
  <ErrorBoundary>
    <MuiThemeProvider theme={Theme}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <Component history={history} />
        </PersistGate>
      </Provider>
    </MuiThemeProvider>
  </ErrorBoundary>
)

const rootElement = document.getElementById('root')

const fixFooterWithNonSafeArea = () => {
  const safeAreaInsetBottom = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom')
  if (parseInt(safeAreaInsetBottom) === 0) {
    const style = document.createElement('style')
    style.innerHTML = '@media(max-width: 991px){body.logged-in .layout__content{bottom: 61px !important;}}'
    document.head.appendChild(style)
  }
}

const fixNativeCookies = async () => {
  try {
    // ↓ MAKES COOKIES WORK, DO NOT REMOVE
    await api.auth.getWhoAmI(appMetaData)
    const { value: sessionId } = await Preferences.get({ key: 'sessionId' })

    if (sessionId) {
      // console.log(`LOGIN | Making native cookies work with sessionId: ${sessionId}`)
      await ApiClient.post(apiUrl('/auth/login'), { sessionId: String(sessionId) })
    }
  } catch (e: any) {
    // console.error('LOGIN | Error with Capacitor login', e)
  }
}

const startApp = async () => {
  if (isNative()) {
    document.body.classList.add('is-native')
  }
  // TODO: What happens if we don't wait for this? - "session expired" happens!!
  await fixNativeCookies()
  // TODO: Do we even need cookies anymore?
  // fixNativeCookies()
  if (isIOS) {
    document.body.classList.add('is-ios')
  }
  fixFooterWithNonSafeArea()
  ReactDOM.render(renderApp(Root), rootElement)
  // Call the element loader after the app has been rendered the first time
  await defineCustomElements(window)
  if (isNative()) {
    await SplashScreen.hide()

    // iOS
    window.addEventListener('statusTap', () => {
      scrollToTop()
    })

    // Android
    App.addListener('backButton', ({ canGoBack }) => {
      const isWelcomePage = window.location.pathname === '/'
      const isChallengesListPage = window.location.pathname === '/challenges'
      if (isWelcomePage || isChallengesListPage) {
        App.exitApp()
      } else {
        window.history.back()
      }
    })

    await dataSync()
    await setUpPushNotifications()

    const { version } = buildVersion || { version: '' }
    const info = await Device.getInfo()
    await log(`Version: ${version} | Platform: ${info.platform} (${info.osVersion})`)
  }
}

const resumeToApp = async () => {
  refreshActivityView()
  document.body.classList.remove('is-syncing')
  // await codePushCheck()
  await dataSync()
}

async function init() {
  await startApp()
  if (isNative()) {
    await App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) await resumeToApp()
    })
    if (Capacitor.getPlatform() === 'ios') {
      await Keyboard.addListener('keyboardWillShow', (info) => {
        const contentEl = document.getElementById('content')
        if (contentEl) {
          const isLoggedIn = document.body.classList.contains('logged-in')
          const isRegistering = document.body.classList.contains('register-user-page')
          contentEl.style.marginBottom = info.keyboardHeight - (isLoggedIn && !isRegistering ? 81 : 0) + 'px'
        }
      })
      await Keyboard.addListener('keyboardWillHide', () => {
        const contentEl = document.getElementById('content')
        if (contentEl) contentEl.style.marginBottom = '0px'

        //const chatModal = document.getElementsByClassName('str-chat__modal--open') as HTMLCollectionOf<HTMLElement>
        //if (chatModal[0]) chatModal[0].style.paddingBottom = '0px'
      })
    }
  }
  if (process.env.NODE_ENV === 'development') {
    if (module.hot) {
      module.hot.accept('./Root/Root', () => {
        const NextRoot = require('./Root/Root').Root
        ReactDOM.render(renderApp(NextRoot), rootElement)
        // Call the element loader after the app has been rendered the first time
        defineCustomElements(window)
      })
    }
  }
}

;(async () => {
  await init()

  // If you want your app to work offline and load faster, you can change
  // unregister() to register() below. Note this comes with some pitfalls.
  // Learn more about service workers: http://bit.ly/CRA-PWA
  await serviceWorker.unregister()
  /*await serviceWorker.register()
  navigator.serviceWorker &&
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload()
    })*/
})()
